// Memoized bar chart (error distribution, revenue-by-run, …). Same themed,
// animation-off treatment as TimeSeriesChart.

import { memo } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";

import { ChartFrame } from "@/components/charts/ChartFrame";
import { usePhone } from "@/app/console/useViewport";
import {
  ANIMATION_ACTIVE,
  AXIS_TICK,
  GRID_PROPS,
  TOOLTIP_CONTENT_STYLE,
  TOOLTIP_LABEL_STYLE,
} from "@/components/charts/theme";

export interface BarSeriesChartProps {
  data: readonly unknown[];
  xKey: string;
  barKey: string;
  name: string;
  color: string;
  height?: number;
  yWidth?: number;
  xTickFormatter?: (value: unknown) => string;
  yTickFormatter?: (value: number) => string;
  tooltipFormatter?: (value: unknown) => [string, string] | string;
  /** Full-screen heading (defaults to the enclosing Card's title). */
  title?: string;
  expandable?: boolean;
}

function BarSeriesChartBase({
  data,
  xKey,
  barKey,
  name,
  color,
  height = 200,
  yWidth = 44,
  xTickFormatter,
  yTickFormatter,
  tooltipFormatter,
  title,
  expandable,
}: BarSeriesChartProps) {
  const phone = usePhone();
  return (
    <ChartFrame height={height} title={title} expandable={expandable}>
      <BarChart data={data as Record<string, unknown>[]} margin={{ top: 8, right: 12, left: 0, bottom: 0 }}>
        <CartesianGrid {...GRID_PROPS} />
        <XAxis
          dataKey={xKey}
          tick={AXIS_TICK}
          tickLine={false}
          minTickGap={phone ? 48 : undefined}
          interval={phone ? "preserveStartEnd" : undefined}
          tickFormatter={xTickFormatter as ((value: string) => string) | undefined}
        />
        <YAxis tick={AXIS_TICK} tickLine={false} width={yWidth} tickFormatter={yTickFormatter} />
        <Tooltip
          cursor={{ fill: "rgba(255,255,255,0.04)" }}
          contentStyle={TOOLTIP_CONTENT_STYLE}
          labelStyle={TOOLTIP_LABEL_STYLE}
          formatter={tooltipFormatter as never}
        />
        <Bar
          dataKey={barKey}
          name={name}
          fill={color}
          radius={[2, 2, 0, 0]}
          isAnimationActive={ANIMATION_ACTIVE}
        />
      </BarChart>
    </ChartFrame>
  );
}

export const BarSeriesChart = memo(BarSeriesChartBase);
